import {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {XHR_LOADING_STATE} from '../../../assets/constants/constants';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';
import {sendAiAgentFeedback} from '../../../redux/aiAgent/aiAgentActions';
import type {AiAgentPendingFeedback} from '../../../redux/aiAgent/aiAgent.type';
import type {TState} from '../../../redux/store.type';
import {clearAgentFeedbackRequest, markAgentFeedbackSent} from '../utils/agentFeedbackRequest';
import {closeAgentPanel} from '../utils/agentPanelState';

/**
 * The feedback is the chat's last step: answered or waved off, the panel closes at once and
 * the record it created has the screen. A toast says the answer arrived; the thank-you also
 * waits in the chat for the next open.
 */
export function useAiAgentFeedback(feedback: AiAgentPendingFeedback) {
    const {t} = useTranslation('aiAgent');
    const [rating, setRating] = useState(0);
    const [text, setText] = useState('');
    const feedbackXHR = useSelector((s: TState) => s.aiAgent?.feedbackXHR);
    const loading = feedbackXHR === XHR_LOADING_STATE;

    const submit = useCallback(async () => {
        if(!rating || loading) {
            return;
        }
        const {sessionId, scope, eid, pid} = feedback;
        const sent = await sendAiAgentFeedback({sessionId, scope, eid, pid, rating, text: text.trim()});
        if(sent) {
            markAgentFeedbackSent();
            closeAgentPanel();
            bounceMessage(t('feedbackThanks'), 'success');
        }
    }, [feedback, rating, text, loading, t]);

    const skip = useCallback(() => {
        clearAgentFeedbackRequest();
        closeAgentPanel();
    }, []);

    return {
        rating,
        setRating,
        text,
        setText,
        loading,
        sent: feedback.status === 'sent',
        submit,
        skip,
    };
}
